import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, AlertCircle, CreditCard, Ban, RefreshCw, Mail, CheckSquare } from 'lucide-react';
import Seo from '../components/Seo';

const TermsPolicies = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 20, 2026";

  const sections = [
    {
      id: "purpose",
      title: "1. Platform Purpose",
      content: "INeedASocialWorker provides a professional platform for job listings, resume visibility, and hiring connections within the social work profession. We offer advertising and exposure services only and do not participate in the hiring decision-making process."
    },
    {
      id: "job-sources",
      title: "2. Job Sources & Data Policy",
      content: "Jobs displayed on the platform are obtained through:",
      list: [
        "Direct employer submissions",
        "Authorized staffing partners",
        "Public government data sources (including USAJOBS)"
      ],
      footer: "INeedASocialWorker.com does not scrape, copy, or republish job postings from third-party job boards or private platforms without authorization."
    },
    {
      id: "federal-jobs",
      title: "3. Federal Job Listings",
      content: "Federal job postings sourced from USAJOBS are provided for informational purposes only. Applicants are redirected to USAJOBS.gov to review full details and submit applications. INeedASocialWorker.com does not process federal job applications.",
      list: [
        "All federal jobs are clearly marked with a 'Federal Job via USAJobs.gov' badge",
        "Applications are submitted directly through the official USAJOBS.gov website",
        "We do not alter or modify federal job descriptions"
      ]
    },
    {
      id: "no-guarantee",
      title: "4. No Guarantee of Results",
      content: "While we work to promote job listings and provide visibility to employers, results are not guaranteed. We do not guarantee:",
      list: [
        "Applicant volume",
        "Candidate qualifications",
        "Interviews or hiring outcomes"
      ],
      footer: "Employers remain solely responsible for hiring decisions, credential verification, and compliance with applicable laws and regulations."
    },
    {
      id: "fees",
      title: "5. Memberships, Fees & Refund Policy",
      content: "All membership fees, featured listings, upgrades, and related services are final and non-refundable.",
      footer: "Early cancellations, lack of applicant response, or dissatisfaction with hiring outcomes do not qualify for refunds or credits."
    },
    {
      id: "featured",
      title: "6. Featured Job Listings",
      content: "Featured or highlighted job listings receive increased visibility within the platform. Placement, exposure, and duration are provided on a best-effort basis and may vary depending on platform activity and user engagement."
    },
    {
      id: "privacy",
      title: "7. Privacy Policy",
      content: "We respect user privacy and collect only the information necessary to operate the platform. This may include account details, job postings, resumes, and platform activity.",
      list: [
        "We do not sell or trade user data.",
        "Limited information may be shared with trusted service providers (such as payment processors or hosting services) strictly for operational and security purposes.",
        "Users are responsible for maintaining the confidentiality of their login credentials."
      ]
    },
    {
      id: "employer",
      title: "8. Employer Responsibilities",
      content: "Employers agree to:",
      list: [
        "Post accurate, lawful, and ethical job listings",
        "Certify they are authorized to recruit for listed positions",
        "Verify candidate credentials independently",
        "Use the platform in compliance with professional and legal standards"
      ],
      footer: "INeedASocialWorker reserves the right to remove listings or suspend accounts that violate these standards. Misrepresentation may result in removal of listings or account termination."
    },
    {
      id: "license-verification",
      title: "9. License Verification",
      content: "INeedASocialWorker.com offers an optional License Verification service for job seekers. License verification reflects publicly available state licensing board records at the time of review.",
      list: [
        "License verification confirms the license was active and in good standing at the time of review",
        "INeedASocialWorker.com is not a licensing authority and does not issue or certify licenses",
        "License numbers are used only for verification and are never displayed publicly",
        "Verification does not guarantee ongoing licensure status"
      ],
      footer: "Employers should continue to follow their own credentialing, background check, and onboarding procedures. License verification is provided as a screening aid only."
    },
    {
      id: "license-reminder-disclaimer",
      title: "9a. License Renewal Reminder Disclaimer",
      content: "INASW license expiration reminders are provided as a courtesy only. While INASW may send reminders at 90, 60, 30, and 7 days before the expiration date entered in a member's account, INASW does not guarantee that reminders will be sent, received, delivered, or received on time.",
      list: [
        "Members are solely responsible for maintaining an active professional license, monitoring their license status and expiration date, completing all renewal requirements, and renewing by the applicable licensing board's deadline",
        "Failure to receive an INASW reminder does not relieve a member of their responsibility to maintain and renew their professional license",
        "INASW is not responsible for missed renewals, expired licenses, late fees, employment consequences, disciplinary action, or other losses resulting from reliance on the reminder service",
        "Members should verify all licensing requirements, deadlines, and license status directly with their applicable state licensing board"
      ],
      footer: "INASW reminders are a convenience and should not be used as the sole method for tracking professional license renewal deadlines."
    },
    {
      id: "anti-scraping",
      title: "10. Prohibited Data Use & Anti-Scraping",
      content: "Job listings, candidate profiles, and all content displayed on this site are protected. The following activities are strictly prohibited:",
      list: [
        "Scraping, crawling, or automated data collection from this website",
        "Indexing, republishing, or redistributing job listings without written authorization",
        "Using bots, scripts, or automated tools to access platform data",
        "Copying job descriptions, candidate information, or proprietary content",
        "Creating derivative databases from platform content"
      ],
      footer: "Violations may result in immediate account termination, legal action, and reporting to relevant authorities. We actively monitor for unauthorized access and data collection."
    },
    {
      id: "disputes",
      title: "11. Dispute Resolution Policy",
      content: "If a dispute arises regarding membership services, listings, or platform access, users must contact support directly before initiating external claims or chargebacks.",
      list: [
        "Disputes do not include dissatisfaction with applicant volume or hiring outcomes.",
        "We reserve the right to review disputes using platform records, agreements, and usage data."
      ]
    },
    {
      id: "chargeback",
      title: "12. Chargeback & Payment Protection",
      content: "Unauthorized chargebacks or payment disputes may result in:",
      list: [
        "Immediate account suspension or termination",
        "Removal of active job listings",
        "Loss of platform access",
        "Collection action for recovered amounts"
      ],
      footer: "We retain records of user activity, IP addresses, and payment agreements to respond to disputes. Fraudulent chargebacks will be reported and pursued."
    },
    {
      id: "termination",
      title: "13. Account Termination",
      content: "We reserve the right to suspend or terminate accounts for violations of these terms, misuse of the platform, or unethical conduct, without obligation to issue refunds."
    },
    {
      id: "updates",
      title: "14. Updates to Terms",
      content: "These Terms & Policies may be updated periodically. Continued use of the platform constitutes acceptance of the most current version."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Terms & Policies"
        description="Terms & Policies for I Need A Social Worker — platform purpose, job data sources, membership fees, license verification, and acceptable use for social work professionals and employers."
        path="/terms"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Legal
          </div>
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-4">
            Terms & Policies
          </h1>
          <p className="text-gray-600">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 mb-8">
          <p className="text-gray-700 leading-relaxed">
            Welcome to INeedASocialWorker. By accessing or using this platform, you agree to the following Terms & Policies. 
            These terms apply to all users, including employers and social workers.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-gray-100 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Table of Contents</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-gray-600 hover:text-primary transition-colors"
              >
                {section.title}
              </a>
            ))}
            <a href="#contact" className="text-sm text-gray-600 hover:text-primary transition-colors">
              15. Contact Information
            </a>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100"
            >
              <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
                {section.title}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {section.content}
              </p>
              {section.list && (
                <ul className="space-y-2 mb-4">
                  {section.list.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-600">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.footer && (
                <p className="text-gray-700 leading-relaxed">
                  {section.footer}
                </p>
              )}
            </div>
          ))}

          {/* Contact Section */}
          <div
            id="contact"
            className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100"
          >
            <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
              15. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions regarding these Terms & Policies, please contact:
            </p>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium text-gray-900">INeedASocialWorker Support</p>
                <a 
                  href="mailto:support@ineedasocialworker.com" 
                  className="text-primary hover:underline"
                >
                  support@ineedasocialworker.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Checkout Confirmation Statement */}
        <div className="bg-secondary/10 rounded-xl p-6 sm:p-8 mt-8">
          <div className="flex items-start gap-3">
            <CheckSquare className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Checkout Confirmation Statement</h3>
              <p className="text-gray-700 text-sm">
                By completing your purchase, you confirm: "I have read and agree to the Terms & Policies. 
                I understand that results are not guaranteed and all payments are non-refundable."
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Use of this site constitutes acceptance of our Terms & Policies. 
            Results are not guaranteed. All fees are non-refundable.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} INeedASocialWorker.com | Powered by Diversified Social Work Solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPolicies;
